import { NgClass, NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, OnDestroy, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { get } from 'lodash';
import {
	BehaviorSubject,
	Observable,
	Subject,
	asyncScheduler,
	debounceTime,
	distinctUntilChanged,
	interval,
	of,
	switchMap,
	takeUntil,
	takeWhile,
} from 'rxjs';
import { FORMLY_FIELD_TYPES } from '@app/constants';
import { LoadingService } from '@app/core/services';
import { AccountSearchType } from '@app/enums/account-search-type.enum';
import { TableConfig } from '@app/interfaces';
import { IAccountSearchModel } from '@app/interfaces/lsb/account-search';
import { AccountModel } from '@app/models/fund-management';
import { FormComponent } from '@app/shared/forms/components';
import { AccountService, AccountStructureService } from '@app/shared/services';
import { isNil } from '@app/utils';
import { ModalComponent } from '../modal/modal.component';

@Component({
	selector: 'app-accounts-search',
	templateUrl: './accounts-search.component.html',
	styleUrls: ['./accounts-search.component.less'],
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [ModalComponent, FormComponent, MatButtonModule, NgClass, MatTooltipModule, NgIf, MatIconModule],
})
export class AccountsSearchComponent implements AfterViewInit, OnDestroy {
	@ViewChild('fc') formComponent: FormComponent;
	@ViewChild('iconButtonTemplate') iconButtonTemplate: TemplateRef<any>;

	/**
	 * AccountSearchModel Input property - the model used to perform account searches.
	 * Note: If not provided, a default model will be created within the component.
	 */
	@Input() AccountSearchModel: IAccountSearchModel = null;
	// @Input() AccountTypes: number[] = []; // Ex: [AccountType.Liability, AccountType.Net]
	// @Input() BankId: number = null;

	constructor(
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly accountService: AccountService,
		private readonly accountStructureService: AccountStructureService,
		private readonly loadingService: LoadingService,
		protected readonly dialog: MatDialog,
		public dialogRef: MatDialogRef<AccountsSearchComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		this.initializeComponent(data);
	}

	ACCTNUM = 'ACCTNUM';
	formlyFormOptions: FormlyFormOptions = {
		formState: {
			mode: 'edit',
			selects: {
				acctData: [],
			},
		},
	};
	form = new UntypedFormGroup({});
	formlyFields: FormlyFieldConfig[] = [];
	model: any = {
		SearchType: this.ACCTNUM,
		SearchAccountNumber: null,
		SearchAccountDescription: null,
		UnifiedAccountMask: null,
		QuestionMarkMask: null,
		Searching: false,
	};

	private readonly searching$ = new BehaviorSubject(false);

	ngAfterViewInit(): void {
		this.formlyFields = this.getFormlyFields();
		// formlyFields is bound in the template and was just changed after the view was checked
		this.changeDetectorRef.detectChanges();
		new Promise<void>((resolve) => {
			this.accountStructureService.getStructure().subscribe((data) => {
				this.model.UnifiedAccountMask = data.accountStructure.UnifiedAccountMask;
				this.model.QuestionMarkMask = !isNil(this.model.UnifiedAccountMask) ? this.model.UnifiedAccountMask.replace(/0/g, '?') : '';
				this.model.SearchAccountNumber = this.model.QuestionMarkMask;
				this.changeDetectorRef.detectChanges();
				this.formlyFields = this.getFormlyFields();
				resolve();
			});
		}).then(() => {
			this.searching$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
				this.model.Searching = result;
			});
		});
	}

	private readonly destroy$ = new Subject();
	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	getFormlyFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'row accounts-search-container',
				fieldGroup: [
					{
						fieldGroupClassName: 'col-xs-12 search-container',
						fieldGroup: [
							{
								fieldGroupClassName: '',
								fieldGroup: [
									{
										type: FORMLY_FIELD_TYPES.radio,
										key: 'SearchType',
										className: 'radio-button-container',
										props: {
											type: 'radio',
											theme: 'default',
											options: [
												{
													label: 'Account #',
													value: this.ACCTNUM,
												},
												{
													label: 'Account Description',
													value: 'description',
												},
											],
											change: () => {
												this.manageSearchControls();
											},
										},
										hooks: {
											onInit: () => this.manageSearchControls(),
										},
									},
								],
							},
							{
								fieldGroupClassName: 'row',
								fieldGroup: [
									{
										key: 'SearchAccountNumber',
										type: FORMLY_FIELD_TYPES.maskedInput,
										className: 'col-xs-6',
										props: {
											label: 'Search by Account #',
											appearance: 'outline',
											floatLabel: 'always',
											questionMarkMask: this.model.QuestionMarkMask,
											maxLength: this.model.QuestionMarkMask?.length,
										},
										expressions: {
											hide: () => this.model?.SearchType !== this.ACCTNUM,
										},
									},
									{
										key: 'SearchAccountDescription',
										type: FORMLY_FIELD_TYPES.input,
										className: 'col-xs-6',
										props: {
											label: 'Search by Description',
											appearance: 'outline',
											floatLabel: 'always',
										},
										expressions: {
											hide: () => this.model?.SearchType === this.ACCTNUM,
										},
									},
									{
										fieldGroupClassName: 'col-xs-1 linq-mt-2',
										fieldGroup: [
											{
												key: 'Spinner',
												type: FORMLY_FIELD_TYPES.formlySpinner,
												props: {
													diameter: 30,
												},
												expressions: {
													hide: (): boolean => this.model.Searching === false,
												},
											},
										],
									},
								],
							},
							{
								fieldGroupClassName: 'row',
								fieldGroup: [
									{
										key: 'ResultsTable',
										type: FORMLY_FIELD_TYPES.formlyTable,
										className: 'col-xs-12 results-table',
										props: {
											noDataText: 'No results found.',
											data: [],
										},
										expressions: {
											'props.config': () => this.tableConfig,
											'props.data': 'formState.selects.acctData',
											'props.noDataText': () => (this.model.Searching ? 'Searching...' : 'No results found.'),
										},
									},
								],
							},
						],
					},
					{
						template: '<br>',
					},
				],
			},
		];
	}

	get tableConfig(): TableConfig<AccountModel> {
		return {
			columns: [
				{
					header: 'Account #',
					key: 'AccountNumber',
				},
				{
					header: 'Description',
					key: 'Description',
				},
				{
					header: 'Type',
					key: 'AccountType',
				},
			],

			stickyHeader: true,
			showRowSelect: false,
			selectedId: null,
			bulkActions: [],
			defaultSortDirection: 'asc',
			defaultSortField: 'AccountNumber',
			defaultPageSize: 100,
			excludePaginator: true,
			onRowClick: (row: AccountModel) => {
				const resultsTable = this.form.get('ResultsTable');
				const resultsTableTemplateOptions = get(resultsTable, '_fields[0].templateOptions', null);
				resultsTableTemplateOptions.config = {
					...resultsTableTemplateOptions.config,
					selectedId: row !== null ? row.Id : null,
				};
				asyncScheduler.schedule(() => {
					this.dialogRef.close(row);
				}, 500);
			},
		};
	}

	/*
	 * initializeComponent - updates the component with data from the injected dialog data.
	 */
	private initializeComponent(data: any): void {
		if (Object.prototype.hasOwnProperty.call(data, 'AccountSearchModel')) {
			this.AccountSearchModel = data.AccountSearchModel;
		} else {
			this.AccountSearchModel = {
				AccountNumber: '',
				Limit: 100,
				BankId: null,
				OnlyActive: true,
				Filter: true, // filter for user's account access
				SearchMode: AccountSearchType.Simple, // Fund is not needed in results
			} as IAccountSearchModel;
		}

		if (isNil(this.AccountSearchModel.AccountTypeIds)) {
			this.AccountSearchModel.AccountTypeIds = [];
		}
	}

	private manageSearchControls(): void {
		let inputCtrl = null;
		const inputCtrlName = this.model.SearchType === this.ACCTNUM ? 'SearchAccountNumber' : 'SearchAccountDescription';
		const selectionRange = this.model.SearchType === this.ACCTNUM ? [0, 0] : null;
		interval(100)
			.pipe(
				takeWhile((_, index) => index < 50 && isNil(inputCtrl)), // Check for up to 5 seconds (50 * 100ms)
				switchMap(() => {
					inputCtrl = this.form.get(inputCtrlName);
					if (inputCtrl) {
						return of(inputCtrl);
					}
					return of(null);
				}),
				switchMap((ctrl: FormControl) => {
					if (ctrl) {
						return this.evaluateControlChanges(ctrl, inputCtrlName, selectionRange);
					}
					return of(null);
				}),
				takeUntil(this.destroy$)
			)
			.subscribe((data) => {
				if (!isNil(data)) {
					this.formlyFormOptions.formState.selects.acctData = data;
					this.searching$.next(false);
				}
			});
	}

	private evaluateControlChanges(ctrl: FormControl, name: string, range: number[]): Observable<any> {
		ctrl.setValue(null);
		ctrl.updateValueAndValidity();
		this.formComponent.setFocus(name, 100, range);

		return ctrl.valueChanges.pipe(
			takeUntil(this.destroy$),
			takeWhile(() => !isNil(this.form.get(name))),
			debounceTime(500),
			distinctUntilChanged(),
			switchMap((value) => {
				if (isNil(value) || value.length === 0) {
					return of([]);
				}
				this.searching$.next(true);
				return this.accountSearch(value);
			})
		);
	}

	private accountSearch(value: string): Observable<any> {
		this.AccountSearchModel.AccountNumber = this.model.SearchType === this.ACCTNUM ? value : '';
		this.AccountSearchModel.Search = this.model.SearchType === this.ACCTNUM ? '' : value;

		this.loadingService.skipNextRequest();
		this.formlyFormOptions.formState.selects.acctData = [];
		return this.accountService.searchAccounts(this.AccountSearchModel).pipe(
			takeUntil(this.destroy$),
			switchMap((data) => {
				this.searching$.next(false);
				return data.isValid ? of(data.accounts) : of([]);
			})
		);
	}
}
